import { useEffect } from "react";
import { Helmet } from "react-helmet";
import "dayjs/locale/ar-sa";
import "dayjs/locale/am";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { NavLink as RouterLink, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import { StyledAccordionSummary } from "../../styles/componentStyled";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import {
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { List, Save } from "@mui/icons-material";

import Toast from "../../components/Layout/Toast";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createEmployee,
  getEmployee,
  resetSelectedEmployee,
  selectProcesses,
} from "./processSlices";
import {
  Employee,
  EmployeeStatus,
  GulfCountries,
  MaritalStatus,
  ReligionTypes,
  Sex,
  EducationLevel,
} from "./types/processTypes";
import { selectPreference } from "../preferences/preferencesSlice";
import { FormikTextField } from "../../components/Layout/FormikTextField";
import { formatReferenceNumber } from "./utils/referenceNumber";
import { Form, Formik } from "formik";
import { createEmployeeSchema } from "./validation";
import { useTranslation } from "react-i18next";
import { selectAuth } from "../auth/authSlice";
import { RegionAutocomplete } from "./components/RegionAutocomplete";
import { BrokerNameAutocomplete } from "./components/BrokerNameAutocomplete";
import { Experience } from "./components/Experience";
import { PassportScanner } from "./components/PassportScanner";
import { addDays, subYears } from "date-fns";

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

// Parses a passport "DD MMM YYYY" date (e.g. "15 JAN 1990") into a Date.
const parsePassportDate = (value?: string): Date | null => {
  if (!value) return null;
  const parts = value.trim().split(/\s+/);
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = MONTHS.indexOf(parts[1].slice(0, 3).toLowerCase());
  const year = parseInt(parts[2], 10);
  if (Number.isNaN(day) || month < 0 || Number.isNaN(year)) return null;
  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const mapSex = (value?: string): Sex | undefined => {
  const v = value?.trim().toLowerCase();
  if (!v) return undefined;
  if (v.startsWith("m")) return Sex.male;
  if (v.startsWith("f")) return Sex.female;
  return undefined;
};

export const CandidateIntake = () => {
  const { id } = useParams() as { id: string };

  const { t, i18n } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const { selectedEmployee, error, success, loading } =
    useAppSelector(selectProcesses);
  const { locale } = useAppSelector(selectPreference);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (id && id.length > 10) {
      dispatch(getEmployee(id));
    } else {
      dispatch(resetSelectedEmployee());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (loading === "pending") {
      document.body.style.opacity = "0.5";
    } else {
      document.body.style.opacity = "1";
    }
  }, [loading]);

  return (
    <div dir={i18n.dir()}>
      <Helmet>
        <title>{`Register Candidate | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Formik
          enableReinitialize={true}
          initialValues={
            selectedEmployee
              ? {
                  ...selectedEmployee,
                  status: EmployeeStatus.pending,
                  dateOfBirth:
                    selectedEmployee.dateOfBirth ?? subYears(new Date(), 25),
                }
              : ({
                  status: EmployeeStatus.pending,
                  dateOfBirth: subYears(new Date(), 25),
                } as Employee)
          }
          validationSchema={createEmployeeSchema}
          onSubmit={(values, actions) => {
            actions.setSubmitting(false);
            if (user && user.company) {
              values = { ...values, owner: user.company };
              if (user.company.parentCompany) {
                values = {
                  ...values,
                  parentCompany: user.company.parentCompany,
                };
              }
            }
            dispatch(createEmployee(values));
          }}
        >
          {(props) => (
            <Form>
              <Stack>
                {/* Toolbar */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                  }}
                >
                  <Tooltip
                    title={t("save") + " " + t("employee") + " " + t("data")}
                  >
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      size="large"
                      disabled={!props.isValid}
                    >
                      <Save />
                      {t("save")}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Employee list">
                    <Button
                      color="secondary"
                      variant="contained"
                      size="large"
                      component={RouterLink}
                      to="/app/employees"
                    >
                      <List />
                      {t("list")}
                    </Button>
                  </Tooltip>
                </Stack>

                {/* Personal & Passport */}
                <Accordion
                  elevation={2}
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                  expanded
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="personal-content"
                    id="personal-header"
                  >
                    <Typography variant="h5" component="span">
                      Register Candidate
                    </Typography>
                  </StyledAccordionSummary>
                  <AccordionDetails
                    sx={{ p: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}
                  >
                    <Grid container spacing={2}>
                      <Grid container item xs={12} md={8} spacing={2}>
                        {/* Meta */}
                        <Grid item md={4} xs={12}>
                          <FormikTextField
                            formikKey="additionalData.appliedCountry"
                            select
                            label={te("For") + " " + te("country")}
                          >
                            {Object.keys(GulfCountries).map((option) => (
                              <MenuItem key={option} value={option}>
                                {te(option)}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Reference Number"
                            value={formatReferenceNumber(
                              selectedEmployee?.referenceNumber,
                            )}
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mt: 1 }}
                          />
                        </Grid>

                        {/* Passport Details */}
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography
                              variant="overline"
                              sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                letterSpacing: 2,
                              }}
                            >
                              Passport Details
                            </Typography>
                          </Divider>
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <PassportScanner
                            onExtracted={(result) => {
                              const filledIds: string[] = [];
                              const setText = (
                                key: string,
                                value: string | undefined,
                                domId?: string,
                              ) => {
                                if (value && value.trim()) {
                                  props.setFieldValue(key, value.trim(), false);
                                  if (domId) filledIds.push(domId);
                                }
                              };

                              setText(
                                "givenName",
                                result.given_names,
                                "givenName",
                              );
                              setText("surName", result.surname, "surName");
                              setText(
                                "passportNumber",
                                result.passport_number,
                                "passportNumber",
                              );
                              setText(
                                "placeOfBirth",
                                result.place_of_birth,
                                "placeOfBirth",
                              );

                              const sex = mapSex(result.sex);
                              if (sex) {
                                props.setFieldValue("sex", sex, false);
                                filledIds.push("sex");
                              }

                              const dob = parsePassportDate(
                                result.date_of_birth,
                              );
                              if (dob)
                                props.setFieldValue("dateOfBirth", dob, false);

                              const issue = parsePassportDate(
                                result.date_of_issue,
                              );
                              if (issue)
                                props.setFieldValue(
                                  "passportIssueDate",
                                  issue,
                                  false,
                                );

                              const expiry = parsePassportDate(
                                result.date_of_expiry,
                              );
                              if (expiry)
                                props.setFieldValue(
                                  "passportExpiryDate",
                                  expiry,
                                  false,
                                );

                              if (result.passportScan)
                                props.setFieldValue(
                                  "passportScan",
                                  result.passportScan,
                                  false,
                                );

                              // Scroll to the first auto-filled field for review.
                              if (filledIds.length) {
                                setTimeout(() => {
                                  document
                                    .getElementById(filledIds[0])
                                    ?.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                }, 100);
                              }
                            }}
                          />
                        </Grid>
                        <Grid container item md={8} xs={12} spacing={2}>
                          <Grid item md={6} xs={12}>
                            <FormikTextField
                              formikKey="givenName"
                              label={t("givenName")}
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <FormikTextField
                              formikKey="surName"
                              label={t("surName")}
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <FormikTextField
                              formikKey="passportNumber"
                              label={t("passportNumber")}
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <FormikTextField
                              formikKey="sex"
                              select
                              label={te("sex")}
                            >
                              {Object.keys(Sex).map((option) => (
                                <MenuItem key={option} value={option}>
                                  {te(option)}
                                </MenuItem>
                              ))}
                            </FormikTextField>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <FormikTextField
                              formikKey="placeOfBirth"
                              label={t("placeOfBirth")}
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={locale}
                            >
                              <DatePicker
                                label={t("dateOfBirth")}
                                views={["year", "month", "day"]}
                                minDate={new Date("1920-01-01")}
                                disableMaskedInput
                                inputFormat="DD-MMM-YYYY"
                                value={props.values.dateOfBirth}
                                onChange={(value) =>
                                  props.setFieldValue(
                                    "dateOfBirth",
                                    value,
                                    true,
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    id="dateOfBirth"
                                    sx={{ mt: 1 }}
                                    fullWidth
                                    size="small"
                                    error={Boolean(
                                      props.touched.dateOfBirth &&
                                      props.errors.dateOfBirth,
                                    )}
                                    helperText={
                                      props.touched.dateOfBirth &&
                                      props.errors.dateOfBirth
                                    }
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={locale}
                            >
                              <DatePicker
                                label={t("dateOfIssue")}
                                views={["year", "month", "day"]}
                                minDate={subYears(new Date(), 10)}
                                maxDate={new Date()}
                                disableMaskedInput
                                inputFormat="DD-MMM-YYYY"
                                value={props.values.passportIssueDate}
                                onChange={(value) => {
                                  props.setFieldValue(
                                    "passportIssueDate",
                                    value,
                                    true,
                                  );
                                  props.setFieldValue(
                                    "passportExpiryDate",
                                    addDays(new Date(value as Date), 1825),
                                    true,
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    id="passportIssueDate"
                                    sx={{ mt: 1 }}
                                    fullWidth
                                    size="small"
                                    error={Boolean(
                                      props.touched.passportIssueDate &&
                                      props.errors.passportIssueDate,
                                    )}
                                    helperText={
                                      props.touched.passportIssueDate &&
                                      props.errors.passportIssueDate
                                    }
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={locale}
                            >
                              <DatePicker
                                label={t("dateOfExpiry")}
                                views={["year", "month", "day"]}
                                minDate={new Date()}
                                disableMaskedInput
                                inputFormat="DD-MMM-YYYY"
                                value={props.values.passportExpiryDate}
                                onChange={(value) =>
                                  props.setFieldValue(
                                    "passportExpiryDate",
                                    value,
                                    true,
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    id="passportExpiryDate"
                                    sx={{ mt: 1 }}
                                    fullWidth
                                    size="small"
                                    error={Boolean(
                                      props.touched.passportExpiryDate &&
                                      props.errors.passportExpiryDate,
                                    )}
                                    helperText={
                                      props.touched.passportExpiryDate &&
                                      props.errors.passportExpiryDate
                                    }
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>

                        {/* Personal Information */}
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography
                              variant="overline"
                              sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                letterSpacing: 2,
                              }}
                            >
                              Personal Information
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <RegionAutocomplete formikProps={props} />
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <FormikTextField
                            formikKey="additionalData.labourId"
                            label={"Labour ID"}
                            sx={{ mt: 1 }}
                          />
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <FormikTextField
                            formikKey="education.educationLevel"
                            select
                            label="Education Level"
                          >
                            {Object.keys(EducationLevel).map((option) => (
                              <MenuItem key={option} value={option as string}>
                                {te(option)}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                        {/* <Grid item md={2} xs={12}>
                          <FormikTextField
                            formikKey="placeOfIssue"
                            label={t("placeOfIssue")}
                            sx={{ mt: 1 }}
                          />
                        </Grid> */}
                        <Grid item md={3} xs={12}>
                          <FormikTextField
                            formikKey="additionalData.religion"
                            select
                            label={te("religion")}
                          >
                            {Object.keys(ReligionTypes).map((option) => (
                              <MenuItem key={option} value={option}>
                                {te(option)}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>

                        <Grid item md={3} xs={12}>
                          <FormikTextField
                            formikKey="additionalData.maritalStatus"
                            select
                            label="Marital Status"
                          >
                            {Object.keys(MaritalStatus).map((option) => (
                              <MenuItem key={option} value={option as string}>
                                {te(option)}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>

                        <Grid item md={3} xs={12}>
                          <FormikTextField
                            formikKey="additionalData.numberOfChildren"
                            label="Number of Children"
                            type="number"
                          />
                        </Grid>
                      </Grid>

                      <Grid item md={1} xs={12}>
                        <Divider orientation="vertical" />
                      </Grid>
                      {/* Contact Information */}
                      <Grid container item xs={12} md={3} spacing={2}>
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography
                              variant="overline"
                              sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                letterSpacing: 2,
                              }}
                            >
                              Contact Information
                            </Typography>
                          </Divider>
                        </Grid>

                        <Grid item xs={12}>
                          <FormikTextField
                            formikKey="address.telephone"
                            label={t("employeeTelephone")}
                          />
                        </Grid>
                        {/* <Grid item xs={12}>
                          <FormikTextField
                            formikKey="address.alternateTelephone"
                            label={t("alternateTelephone")}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormikTextField
                            formikKey="address.streetAddress"
                            label={t("address")}
                          />
                        </Grid> */}
                        <Grid item xs={12}>
                          <FormikTextField
                            formikKey="contactPerson.displayName"
                            label="Contact Name"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormikTextField
                            formikKey="contactPerson.address.telephone"
                            label={t("contactTelephone")}
                          />
                        </Grid>
                        {/* <Grid item xs={12}>
                          <FormikTextField
                            formikKey="contactPerson.address.streetAddress"
                            label="Contact Address"
                          />
                        </Grid> */}
                        <Grid item xs={12}>
                          <BrokerNameAutocomplete formikProps={props} />
                        </Grid>
                        <Grid item xs={12}>
                          <FormikTextField
                            formikKey="contactPerson.address.alternateTelephone"
                            label="Broker Telephone"
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider textAlign="left">
                          <Typography
                            variant="overline"
                            sx={{
                              fontWeight: 700,
                              color: "primary.main",
                              letterSpacing: 2,
                            }}
                          >
                            Experience
                          </Typography>
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <Experience formikData={props} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
      {error && <Toast severity="error">{error.message}</Toast>}
      {success && <Toast severity="success">{success.message}</Toast>}
    </div>
  );
};
